import React, { Fragment, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import loginImg from '../../assets/login-signup.jpg';
import logo from '../../assets/Logo.svg';
import { Input } from '../../components/Input';
import { register } from '../../redux/slices/userSlice';
import { ROUTES } from '../../constants/routes';

// ? This page doesn't seem to be used anywhere in the app

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signUpSuccess = useSelector((state) => state.users.signUpSuccess);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    address: '',
    practices: [],
  });

  const [isMatch, setIsMatch] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const practiceOptions = ['Compost', 'BioChar', 'Cover Crops'];

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { text: 'Very Weak', color: 'text-red-500' };
      case 2:
        return { text: 'Weak', color: 'text-red-400' };
      case 3:
        return { text: 'Fair', color: 'text-yellow-500' };
      case 4:
        return { text: 'Good', color: 'text-blue-500' };
      case 5:
      case 6:
        return { text: 'Strong', color: 'text-green-500' };
      default:
        return { text: '', color: '' };
    }
  };

  // Validation functions
  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'firstName':
        if (!value.trim()) {
          newErrors.firstName = 'First name is required';
        } else if (value.trim().length < 2) {
          newErrors.firstName = 'First name must be at least 2 characters';
        } else {
          delete newErrors.firstName;
        }
        break;
      case 'lastName':
        if (!value.trim()) {
          newErrors.lastName = 'Last name is required';
        } else if (value.trim().length < 2) {
          newErrors.lastName = 'Last name must be at least 2 characters';
        } else {
          delete newErrors.lastName;
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!emailRegex.test(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      case 'password':
        const strength = calculatePasswordStrength(value);
        setPasswordStrength(strength);
        if (!value) {
          newErrors.password = 'Password is required';
        } else if (value.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        } else if (strength < 3) {
          newErrors.password =
            'Password is too weak. Please include uppercase, lowercase, and numbers';
        } else {
          delete newErrors.password;
        }
        break;
      case 'phoneNumber':
        if (value && !/^\d{10}$/.test(value)) {
          newErrors.phoneNumber = 'Phone number must be 10 digits';
        } else {
          delete newErrors.phoneNumber;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      setFormData({ ...formData, [name]: numericValue });
      validateField(name, numericValue);
    } else if (name === 'practices') {
      const isChecked = e.target.checked;
      const practice = e.target.value;
      setFormData((prevData) => {
        if (isChecked) {
          return { ...prevData, practices: [...prevData.practices, practice] };
        } else {
          return {
            ...prevData,
            practices: prevData.practices.filter((p) => p !== practice),
          };
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
      validateField(name, value);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(register(formData));
      if (result.payload) {
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/farmshare');
        }, 2000);
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (signUpSuccess) {
    navigate('/farmshare');
  }

  useEffect(() => {
    if (formData.password !== confirmPassword) {
      setIsMatch(false);
    } else {
      setIsMatch(true);
    }
  }, [confirmPassword, formData.password]);

  return (
    <Fragment>
      <ToastContainer autoClose={2000} />
      <div className="grid grid-cols-1 sm:grid-cols-2 w-full">
        <div className="hidden sm:block">
          <img className="w-full h-full object-cover" src={loginImg} alt="" />
        </div>

        <div className="bg-gray-100 flex flex-col justify-center">
          {showSuccess ? (
            <div className="max-w-[500px] w-full mx-auto bg-white p-8 text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Account Created Successfully!
                </h2>
                <p className="text-gray-600 mb-4">
                  Welcome to Farmshare, {formData.firstName}! You're being redirected to the
                  equipment page...
                </p>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="max-w-[500px] w-full mx-auto bg-white p-4">
              <Link to={ROUTES.HOME}>
                <img src={logo} alt="logo" />
              </Link>
              <div className="flex-col">
                <div className="px-5">
                  <Input
                    name="firstName"
                    type="text"
                    label="First Name:"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                  )}

                  <Input
                    name="email"
                    type="email"
                    label="Email:"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}

                  <Input
                    name="password"
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  {formData.password && (
                    <div className="mt-1">
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5, 6].map((level) => (
                            <div
                              key={level}
                              className={`h-2 w-4 rounded ${
                                level <= passwordStrength
                                  ? passwordStrength <= 2
                                    ? 'bg-red-400'
                                    : passwordStrength <= 3
                                      ? 'bg-yellow-400'
                                      : passwordStrength <= 4
                                        ? 'bg-blue-400'
                                        : 'bg-green-400'
                                  : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <span
                          className={`text-sm font-medium ${getPasswordStrengthText(passwordStrength).color}`}
                        >
                          {getPasswordStrengthText(passwordStrength).text}
                        </span>
                      </div>
                    </div>
                  )}
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                  )}

                  <Input
                    name="confirmPassword"
                    type="password"
                    label="Confirm Password:"
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                    }}
                  />
                  {(errors.confirmPassword || (!isMatch && confirmPassword)) && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword || 'Passwords do not match'}
                    </p>
                  )}
                  <Input
                    name="address"
                    type="text"
                    label="Address:"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="px-5">
                  <Input
                    name="lastName"
                    type="text"
                    label="Last Name:"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                  )}

                  <Input
                    name="phoneNumber"
                    type="tel"
                    label="Phone Number:"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                  {errors.phoneNumber && (
                    <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
                  )}

                  {/* Practices */}
                  <div className="flex flex-col py-2">
                    <label>Practices :</label>
                    {practiceOptions.map((practice) => (
                      <label key={practice}>
                        <input
                          className="border px-8"
                          type="checkbox"
                          name="practices"
                          value={practice}
                          onChange={handleInputChange}
                          checked={formData.practices.includes(practice)}
                        />
                        {practice}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <button
                disabled={!isMatch || isSubmitting || Object.keys(errors).length > 0}
                type="submit"
                className={
                  'border w-full my-5 py-2 ' +
                  (isMatch && !isSubmitting && Object.keys(errors).length === 0
                    ? 'bg-lime-700 text-white hover:bg-lime-600'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed')
                }
              >
                {isSubmitting ? 'Creating Account...' : 'Sign Up'}
              </button>
              <Link to={ROUTES.LOGIN}>
                <button
                  className={'border w-full my-5 py-2 bg-sky-500 text-white hover:bg-sky-300'}
                >
                  Login
                </button>
              </Link>
            </form>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export { Signup };
